import { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

import { PageWithTitle } from 'src/Common/components';
import {
  useUnwindingRules,
  useActivateRule,
  usePauseRule,
  useDeleteRule
} from '../api/useUnwinding';
import { UnwindingRule } from '../types/unwinding.types';
import { CreateRuleDialog } from '../components/unwinding/CreateRuleDialog';
import { LiveStatusIndicator } from '../components/unwinding/LiveStatusIndicator';
import { RuleDetailPanel } from '../components/unwinding/RuleDetailPanel';
import {
  useUnwindingSocket,
  UnwindOrderState
} from '../hooks/useUnwindingSocket';

const formatUsd = (value: number | undefined) => {
  if (value == null || isNaN(value)) return '-';
  return '$' + value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatPercent = (value: number | undefined | null) => {
  if (value == null || isNaN(value)) return '-';
  return value.toFixed(2) + '%';
};

const statusChipColor = (
  status: UnwindingRule['status']
): 'success' | 'default' | 'info' | 'error' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'paused':
      return 'default';
    case 'completed':
      return 'info';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const headerSx = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 1,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em'
};

const cellSx = { fontSize: '0.8rem', py: 0.8 } as const;

export const UnwindingPage = () => {
  const { data: rules, isLoading } = useUnwindingRules();
  const activateMutation = useActivateRule();
  const pauseMutation = usePauseRule();
  const deleteMutation = useDeleteRule();
  const { isConnected, getState } = useUnwindingSocket();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRule, setEditRule] = useState<UnwindingRule | null>(null);
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);

  // Stats
  const stats = useMemo(() => {
    if (!rules) return { total: 0, active: 0, volume: 0, recovered: 0 };
    return {
      total: rules.length,
      active: rules.filter(r => r.status === 'active').length,
      volume: rules.reduce((s, r) => s + r.totalVolumeExecutedUsd, 0),
      recovered: rules.reduce((s, r) => {
        // Capital recovered is approximated by volume executed
        return s + r.totalVolumeExecutedUsd;
      }, 0)
    };
  }, [rules]);

  const handleToggleStatus = (rule: UnwindingRule) => {
    if (rule.status === 'active') {
      pauseMutation.mutate(rule._id);
    } else if (rule.status === 'paused') {
      activateMutation.mutate(rule._id);
    }
  };

  const handleEdit = (rule: UnwindingRule) => {
    setEditRule(rule);
    setDialogOpen(true);
  };

  const handleDelete = (rule: UnwindingRule) => {
    if (window.confirm(`Delete unwinding rule for "${rule.pairName}"?`)) {
      deleteMutation.mutate(rule._id);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditRule(null);
  };

  return (
    <PageWithTitle title="Position Unwinding">
      {/* Stats bar */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`Total Rules: ${stats.total}`}
          size="small"
          sx={{ fontSize: '0.75rem' }}
        />
        <Chip
          label={`Active: ${stats.active}`}
          size="small"
          color="success"
          sx={{ fontSize: '0.75rem' }}
        />
        <Chip
          label={`Volume: ${formatUsd(stats.volume)}`}
          size="small"
          sx={{ fontSize: '0.75rem' }}
        />
        <Chip
          icon={
            <FiberManualRecordIcon
              sx={{ fontSize: '0.6rem !important' }}
            />
          }
          label={isConnected ? 'Live' : 'Disconnected'}
          size="small"
          color={isConnected ? 'success' : 'default'}
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ fontSize: '0.75rem' }}
        >
          Create Rule
        </Button>
      </Stack>

      {/* Rules table */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : !rules || rules.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
            No unwinding rules yet. Create one to start unwinding positions.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx} />
                <TableCell sx={headerSx}>Pair Name</TableCell>
                <TableCell sx={headerSx} align="right">
                  Kalshi TNA%
                </TableCell>
                <TableCell sx={headerSx} align="right">
                  Poly TNA%
                </TableCell>
                <TableCell sx={headerSx} align="right">
                  Max Volume
                </TableCell>
                <TableCell sx={headerSx} align="center">
                  Status
                </TableCell>
                <TableCell sx={headerSx} align="right">
                  Progress
                </TableCell>
                <TableCell sx={headerSx} align="right">
                  Volume Executed
                </TableCell>
                <TableCell sx={headerSx} align="center">
                  Live State
                </TableCell>
                <TableCell sx={headerSx} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map(rule => (
                <RuleRow
                  key={rule._id}
                  rule={rule}
                  liveState={getState(rule._id)}
                  expanded={expandedRuleId === rule._id}
                  onToggleExpand={() =>
                    setExpandedRuleId(
                      expandedRuleId === rule._id ? null : rule._id
                    )
                  }
                  onToggleStatus={() => handleToggleStatus(rule)}
                  onEdit={() => handleEdit(rule)}
                  onDelete={() => handleDelete(rule)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <CreateRuleDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editRule={editRule}
      />
    </PageWithTitle>
  );
};

/* ------------------------------------------------------------------ */

interface RuleRowProps {
  rule: UnwindingRule;
  liveState?: UnwindOrderState;
  expanded: boolean;
  onToggleExpand: () => void;
  onToggleStatus: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const RuleRow = ({
  rule,
  liveState,
  expanded,
  onToggleExpand,
  onToggleStatus,
  onEdit,
  onDelete
}: RuleRowProps) => {
  const kalshiTotal = rule.kalshiConfig?.contractsToSell ?? 0;
  const polyTotal = rule.polymarketConfig?.contractsToSell ?? 0;
  const totalTarget = kalshiTotal + polyTotal;
  const totalSold = rule.contractsSoldKalshi + rule.contractsSoldPolymarket;

  const canToggle = rule.status === 'active' || rule.status === 'paused';

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
          cursor: 'pointer'
        }}
        onClick={onToggleExpand}
      >
        <TableCell sx={{ ...cellSx, width: 32, px: 0.5 }}>
          <IconButton size="small" sx={{ p: 0.3 }}>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: '1rem' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
            )}
          </IconButton>
        </TableCell>
        <TableCell sx={cellSx}>
          <Typography
            sx={{ fontSize: '0.8rem', fontWeight: 500 }}
            noWrap
            title={rule.pairName}
          >
            {rule.pairName}
          </Typography>
        </TableCell>
        <TableCell sx={cellSx} align="right">
          {rule.kalshiConfig
            ? formatPercent(rule.kalshiConfig.targetTnaPercent)
            : '-'}
        </TableCell>
        <TableCell sx={cellSx} align="right">
          {rule.polymarketConfig
            ? formatPercent(rule.polymarketConfig.targetTnaPercent)
            : '-'}
        </TableCell>
        <TableCell sx={cellSx} align="right">
          {formatUsd(rule.maxVolumeUsd)}/{rule.volumePeriod}
        </TableCell>
        <TableCell sx={cellSx} align="center">
          <Chip
            label={rule.status}
            size="small"
            color={statusChipColor(rule.status)}
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              height: 20,
              borderRadius: '4px',
              textTransform: 'capitalize'
            }}
          />
        </TableCell>
        <TableCell sx={cellSx} align="right">
          <Typography sx={{ fontSize: '0.8rem' }}>
            {totalTarget > 0 ? `${totalSold}/${totalTarget}` : '-'}
          </Typography>
        </TableCell>
        <TableCell sx={cellSx} align="right">
          {formatUsd(rule.totalVolumeExecutedUsd)}
        </TableCell>
        <TableCell sx={cellSx} align="center">
          <LiveStatusIndicator liveState={liveState} />
        </TableCell>
        <TableCell sx={cellSx} align="center">
          <Stack
            direction="row"
            spacing={0.3}
            justifyContent="center"
            onClick={e => e.stopPropagation()}
          >
            {canToggle && (
              <Tooltip
                title={rule.status === 'active' ? 'Pause' : 'Activate'}
              >
                <IconButton size="small" onClick={onToggleStatus}>
                  {rule.status === 'active' ? (
                    <PauseIcon sx={{ fontSize: '0.95rem' }} />
                  ) : (
                    <PlayArrowIcon sx={{ fontSize: '0.95rem' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit}>
                <EditIcon sx={{ fontSize: '0.95rem' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onDelete}>
                <DeleteIcon sx={{ fontSize: '0.95rem', color: 'error.main' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
      {/* Expanded execution log */}
      <TableRow>
        <TableCell sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }} colSpan={10}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <RuleDetailPanel ruleId={rule._id} liveState={liveState} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
